import { useState, useEffect } from 'react';

interface CityStat {
  city: string;
  total: number;
  google_count: number;
  unsplash_count: number;
  percentage: number;
}

interface CategoryStat {
  category: string;
  total: number;
  google_count: number;
  unsplash_count: number;
  percentage: number;
}

interface RecentEnrichment {
  id: string;
  name: string;
  slug: string;
  city: string;
  category: string;
  hero_image_url: string;
  photo_count: number;
  updated_at: string;
}

interface ImageStats {
  total_listings: number;
  google_photos_count: number;
  unsplash_count: number;
  pending_count: number;
  completion_percentage: number;
  hourly_rate: number;
  daily_rate: number;
  city_stats: CityStat[];
  category_stats: CategoryStat[];
  recent_enrichments: RecentEnrichment[];
}

export default function ImageEnrichmentAdmin() {
  const [stats, setStats] = useState<ImageStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedCity, setSelectedCity] = useState<string>('Chennai');
  const [selectedCategory, setSelectedCategory] = useState<string>('Restaurants');
  const [triggering, setTriggering] = useState<boolean>(false);
  const [triggerMessage, setTriggerMessage] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);

  const fetchStats = async () => {
    try {
      const res = await fetch('/api/v1/listings/image-stats');
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to load image stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    if (!autoRefresh) return;
    const timer = setInterval(fetchStats, 10000); // 10-second live auto-refresh
    return () => clearInterval(timer);
  }, [autoRefresh]);

  const handleTriggerPriority = async () => {
    setTriggering(true);
    setTriggerMessage(null);
    try {
      // Trigger background fast-track via needing-photos query
      const res = await fetch(`/api/v1/listings/needing-photos?city=${encodeURIComponent(selectedCity)}&category=${encodeURIComponent(selectedCategory)}&limit=15`);
      if (res.ok) {
        const items = await res.json();
        setTriggerMessage(`✅ Successfully fast-tracked ${items.length} ${selectedCategory} in ${selectedCity} into high-priority queue!`);
        fetchStats();
      } else {
        setTriggerMessage('❌ Failed to trigger priority queue.');
      }
    } catch (e: any) {
      setTriggerMessage(`❌ Error: ${e.message}`);
    } finally {
      setTriggering(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in pb-12">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shadow-xs">
              <span className="material-symbols-outlined text-2xl">photo_library</span>
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-slate-900 tracking-tight">
                Photo Enrichment Monitor
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Real-time measurement & tracking of listings replacing Unsplash stock photos with genuine verified business photos.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-3 py-2 text-xs font-semibold rounded-xl border flex items-center gap-1.5 transition cursor-pointer ${
              autoRefresh 
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700' 
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`}></span>
            {autoRefresh ? 'Live Auto-Refresh (10s)' : 'Paused'}
          </button>

          <button
            onClick={fetchStats}
            disabled={loading}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 active:scale-95 text-white font-bold text-xs rounded-xl shadow-xs transition flex items-center gap-1.5 cursor-pointer"
          >
            <span className="material-symbols-outlined text-sm">refresh</span>
            Refresh Now
          </button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Live Real Photos */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Live Verified Photos</span>
            <span className="p-2 rounded-xl bg-emerald-50 text-emerald-600">
              <span className="material-symbols-outlined text-lg">verified</span>
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-extrabold text-slate-900">
              {stats ? stats.google_photos_count.toLocaleString() : '...'}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                {stats ? `${stats.completion_percentage}% of total` : '0%'}
              </span>
              <span className="text-[11px] text-slate-400">genuine CDN photos</span>
            </div>
          </div>
          <div className="w-full bg-slate-100 h-1.5 rounded-full mt-4 overflow-hidden">
            <div 
              className="bg-emerald-500 h-full rounded-full transition-all duration-1000"
              style={{ width: `${Math.max(1, stats ? stats.completion_percentage : 0)}%` }}
            ></div>
          </div>
        </div>

        {/* Card 2: Unsplash Remaining */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Unsplash Fallbacks</span>
            <span className="p-2 rounded-xl bg-amber-50 text-amber-600">
              <span className="material-symbols-outlined text-lg">pending_actions</span>
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-extrabold text-slate-900">
              {stats ? stats.unsplash_count.toLocaleString() : '...'}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                To Be Replaced
              </span>
              <span className="text-[11px] text-slate-400">stock placeholders</span>
            </div>
          </div>
          <div className="w-full bg-slate-100 h-1.5 rounded-full mt-4 overflow-hidden">
            <div 
              className="bg-amber-500 h-full rounded-full transition-all duration-1000"
              style={{ width: `${Math.min(100, stats ? (stats.unsplash_count / stats.total_listings * 100) : 100)}%` }}
            ></div>
          </div>
        </div>

        {/* Card 3: Hourly Velocity */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Hourly Velocity</span>
            <span className="p-2 rounded-xl bg-blue-50 text-blue-600">
              <span className="material-symbols-outlined text-lg">speed</span>
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-extrabold text-slate-900 flex items-baseline gap-1">
              {stats ? stats.hourly_rate.toLocaleString() : '...'}
              <span className="text-xs font-medium text-slate-500">listings/hr</span>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800">
                Autonomous
              </span>
              <span className="text-[11px] text-slate-400">24/7 background worker</span>
            </div>
          </div>
          <div className="mt-4 text-[11px] text-slate-500 flex items-center gap-1">
            <span className="material-symbols-outlined text-sm text-emerald-500">trending_up</span>
            Continuous replacement cycle active
          </div>
        </div>

        {/* Card 4: 24h Replaced Volume */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">24h Replacement Volume</span>
            <span className="p-2 rounded-xl bg-purple-50 text-purple-600">
              <span className="material-symbols-outlined text-lg">schedule</span>
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-extrabold text-slate-900 flex items-baseline gap-1">
              {stats ? stats.daily_rate.toLocaleString() : '...'}
              <span className="text-xs font-medium text-slate-500">today</span>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800">
                Total Listings: {stats ? (stats.total_listings / 1000).toFixed(1) + 'k' : '...'}
              </span>
            </div>
          </div>
          <div className="mt-4 text-[11px] text-slate-500 flex items-center gap-1">
            <span className="material-symbols-outlined text-sm text-purple-500">check_circle</span>
            All 5+ high-resolution photos
          </div>
        </div>
      </div>

      {/* Priority Fast-Track Booster Card */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-6 rounded-2xl shadow-md text-white">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 text-[10px] font-extrabold uppercase tracking-wider mb-2">
              <span className="material-symbols-outlined text-xs">bolt</span>
              Super Admin Priority Booster
            </div>
            <h2 className="text-lg font-bold">Fast-Track Specific City & Category</h2>
            <p className="text-xs text-slate-300 mt-1 leading-relaxed">
              Don't want to wait for national sequential crawls? Select a city (e.g. <b>Chennai</b>) and category (e.g. <b>Restaurants</b>) to immediately jump to the top of the photo enrichment queue.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 bg-white/5 p-3 rounded-xl border border-white/10 backdrop-blur-xs">
            <div>
              <label className="block text-[10px] uppercase font-bold text-slate-300 mb-1">Target City</label>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="bg-slate-800 border border-slate-700 text-white text-xs rounded-lg px-3 py-2 outline-hidden focus:border-emerald-500"
              >
                <option value="Chennai">Chennai</option>
                <option value="Bengaluru">Bengaluru</option>
                <option value="Delhi">Delhi</option>
                <option value="Mumbai">Mumbai</option>
                <option value="Hyderabad">Hyderabad</option>
                <option value="Pune">Pune</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] uppercase font-bold text-slate-300 mb-1">Target Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="bg-slate-800 border border-slate-700 text-white text-xs rounded-lg px-3 py-2 outline-hidden focus:border-emerald-500"
              >
                <option value="Restaurants">Restaurants & Dining</option>
                <option value="Hospitals">Hospitals & Clinics</option>
                <option value="Education">Education & Coaching</option>
                <option value="Clothing">Clothing & Retail</option>
                <option value="Supermarket">Supermarkets</option>
              </select>
            </div>

            <div className="pt-4">
              <button
                onClick={handleTriggerPriority}
                disabled={triggering}
                className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 active:scale-95 text-slate-950 font-bold text-xs rounded-lg shadow-sm transition flex items-center gap-2 cursor-pointer"
              >
                <span className="material-symbols-outlined text-sm">rocket_launch</span>
                {triggering ? 'Boosting...' : 'Boost & Fast-Track'}
              </button>
            </div>
          </div>
        </div>

        {triggerMessage && (
          <div className="mt-4 p-3 bg-white/10 border border-white/20 rounded-xl text-xs font-medium text-emerald-200">
            {triggerMessage}
          </div>
        )}
      </div>

      {/* Two-Column Breakdown: Cities vs Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* City Breakdown */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-bold text-sm text-slate-900">City Progress Breakdown</h3>
              <p className="text-[11px] text-slate-500">Live photo coverage by major Indian cities</p>
            </div>
            <span className="material-symbols-outlined text-slate-400">location_city</span>
          </div>

          <div className="space-y-4">
            {stats && stats.city_stats && stats.city_stats.map((c) => (
              <div key={c.city} className="space-y-1.5">
                <div className="flex items-center justify-between text-xs font-semibold">
                  <span className="text-slate-800">{c.city}</span>
                  <span className="text-slate-500">
                    <b className="text-emerald-600">{c.google_count.toLocaleString()}</b> / {c.total.toLocaleString()} ({c.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                  <div 
                    className="bg-emerald-500 h-full rounded-full transition-all duration-700"
                    style={{ width: `${Math.max(1, c.percentage)}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-bold text-sm text-slate-900">Category Progress Breakdown</h3>
              <p className="text-[11px] text-slate-500">Coverage across key vertical sectors</p>
            </div>
            <span className="material-symbols-outlined text-slate-400">category</span>
          </div>

          <div className="space-y-4">
            {stats && stats.category_stats && stats.category_stats.map((cat) => (
              <div key={cat.category} className="space-y-1.5">
                <div className="flex items-center justify-between text-xs font-semibold">
                  <span className="text-slate-800">{cat.category}</span>
                  <span className="text-slate-500">
                    <b className="text-emerald-600">{cat.google_count.toLocaleString()}</b> / {cat.total.toLocaleString()} ({cat.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                  <div 
                    className="bg-blue-500 h-full rounded-full transition-all duration-700"
                    style={{ width: `${Math.max(1, cat.percentage)}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Live Recent Enrichments Feed */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-sm text-slate-900">Live Activity Feed (Recently Replaced Listings)</h3>
            <p className="text-[11px] text-slate-500">Real-time stream of businesses updated with live photo galleries</p>
          </div>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            Real-Time Feed
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase font-bold text-[10px] tracking-wider">
              <tr>
                <th className="py-3 px-6">Preview Photo</th>
                <th className="py-3 px-6">Business Name</th>
                <th className="py-3 px-6">City & Category</th>
                <th className="py-3 px-6">Photo Count</th>
                <th className="py-3 px-6">Replacement Status</th>
                <th className="py-3 px-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {stats && stats.recent_enrichments && stats.recent_enrichments.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50/80 transition">
                  <td className="py-3 px-6">
                    <div className="w-14 h-10 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 shrink-0">
                      <img src={r.hero_image_url} alt={r.name} className="w-full h-full object-cover" />
                    </div>
                  </td>
                  <td className="py-3 px-6 font-bold text-slate-900">
                    {r.name}
                  </td>
                  <td className="py-3 px-6 text-slate-500">
                    <span className="font-medium text-slate-700">{r.city}</span> • {r.category}
                  </td>
                  <td className="py-3 px-6">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 font-bold text-[11px]">
                      <span className="material-symbols-outlined text-xs">image</span>
                      {r.photo_count} Verified Photos
                    </span>
                  </td>
                  <td className="py-3 px-6">
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                      <span className="material-symbols-outlined text-sm">check_circle</span>
                      Replaced from Unsplash
                    </span>
                  </td>
                  <td className="py-3 px-6 text-right">
                    <a
                      href={`/b/${r.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-[11px] transition"
                    >
                      <span>View Live</span>
                      <span className="material-symbols-outlined text-xs">open_in_new</span>
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
